import { useRef } from "react";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { CurrencyList } from "../../utils/StaticData";
import moment from "moment";
import "./TargetPI.css";

const dmy = (v) => (v ? moment(v).format("DD-MM-YYYY") : "—");
const cap = (p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : "—");

/**
 * Group breakdown: a target assigned to multiple users is shown as ONE row in
 * the list; this modal reveals the user-wise split. Each member can be opened
 * to manage their own Order Booking / Sales entries. The breakdown (incl. a
 * totals row) is also exportable to Excel.
 */
export default function TargetPIGroupModal({ group, canEditRow, onOpenMember, onDeleteMember, onClose }) {
  const ccy = group.currency || "INR";
  const symbol = CurrencyList.find((c) => c.code === ccy)?.symbol || "₹";
  const money = (v) => `${symbol}${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(v) || 0)}`;
  const exporter = useRef();

  const members = group.members || [];
  const totals = members.reduce(
    (a, m) => {
      a.assigned += Number(m.assigned_amount) || 0;
      a.booking += Number(m.order_booking) || 0;
      a.sales += Number(m.achievement_sales) || 0;
      a.open += Number(m.open_order) || 0;
      return a;
    },
    { assigned: 0, booking: 0, sales: 0, open: 0 }
  );
  const totalPct = totals.assigned > 0 ? Number(((totals.sales / totals.assigned) * 100).toFixed(2)) : 0;
  const pctClass = (p) => (p >= 100 ? "bg-success" : p >= 75 ? "bg-warning text-dark" : "bg-danger");

  // Flat rows for Excel: members + a Total row.
  const exportData = [
    ...members.map((m) => ({
      employee_name: m.employee_name,
      frequency: cap(m.period),
      assigned_amount: Number(m.assigned_amount) || 0,
      order_booking: Number(m.order_booking) || 0,
      achievement_sales: Number(m.achievement_sales) || 0,
      open_order: Number(m.open_order) || 0,
      achievement_pct: Number(m.achievement_pct) || 0,
    })),
    {
      employee_name: "TOTAL",
      frequency: "",
      assigned_amount: totals.assigned,
      order_booking: totals.booking,
      achievement_sales: totals.sales,
      open_order: totals.open,
      achievement_pct: totalPct,
    },
  ];

  const downloadExcel = () => {
    if (exporter.current) {
      exporter.current.save(exportData);
    }
  };

  const fileName = `${(group.target_name || "Target")}_breakdown_${moment().format("YYYY-MM-DD")}.xlsx`;

  return (
    <div className="pi-modal-overlay" onClick={onClose}>
      <div className="pi-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pi-modal-header">
          <div className="pi-modal-head-main">
            <div className="pi-modal-target">
              <i className="fas fa-layer-group me-2"></i>
              <span className="pi-modal-target-name">{group.target_name}</span>
              <span className="pi-modal-emp">· {members.length} employees</span>
            </div>
            <div className="pi-modal-range">
              <i className="fas fa-calendar-alt me-1"></i>
              Target period: <strong>{dmy(group.from_date)}</strong> &rarr; <strong>{dmy(group.to_date)}</strong>
            </div>
          </div>
          <button className="btn-close" onClick={onClose} aria-label="Close"></button>
        </div>

        <div className="pi-modal-body">
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
            <div className="text-muted small">
              <i className="fas fa-info-circle me-1"></i>
              User-wise breakdown. Click <i className="fas fa-eye mx-1"></i> to view or edit a user's bookings &amp; sales.
            </div>
            <button className="btn btn-outline-success btn-sm" onClick={downloadExcel}>
              <i className="fas fa-file-excel me-1"></i> Download
            </button>
          </div>

          {/* Hidden Excel exporter for the breakdown */}
          <ExcelExport data={exportData} ref={exporter} fileName={fileName}>
            <ExcelExportColumn field="employee_name" title="Employee" width={160} />
            <ExcelExportColumn field="frequency" title="Frequency" width={110} />
            <ExcelExportColumn field="assigned_amount" title={`Assigned (${ccy})`} width={130} />
            <ExcelExportColumn field="order_booking" title={`Order Booking (${ccy})`} width={140} />
            <ExcelExportColumn field="achievement_sales" title={`Achievement (${ccy})`} width={140} />
            <ExcelExportColumn field="open_order" title={`Open Order (${ccy})`} width={130} />
            <ExcelExportColumn field="achievement_pct" title="Achievement %" width="120" />
          </ExcelExport>

          <div className="pi-table-card">
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Employee</th>
                    <th>Frequency</th>
                    <th className="text-end">Assigned</th>
                    <th className="text-end">Order Booking</th>
                    <th className="text-end">Achievement</th>
                    <th className="text-end">Open Order</th>
                    <th className="text-center">Ach. %</th>
                    <th className="text-center" style={{ width: 90 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => {
                    const pct = Number(m.achievement_pct) || 0;
                    return (
                      <tr key={m.pi_target_id}>
                        <td>{m.employee_name}</td>
                        <td>{cap(m.period)}</td>
                        <td className="text-end">{money(m.assigned_amount)}</td>
                        <td className="text-end">{money(m.order_booking)}</td>
                        <td className="text-end">{money(m.achievement_sales)}</td>
                        <td className="text-end">{money(m.open_order)}</td>
                        <td className="text-center"><span className={`pi-badge ${pctClass(pct)}`}>{pct}%</span></td>
                        <td className="text-center">
                          <div className="pi-actions">
                            <button className="btn btn-sm btn-outline-primary pi-act-btn" title="View / edit detail" onClick={() => onOpenMember(m)}>
                              <i className="fas fa-eye"></i>
                            </button>
                            {canEditRow(m) && onDeleteMember && (
                              <button className="btn btn-sm btn-outline-danger pi-act-btn" title="Delete this user's target" onClick={() => onDeleteMember(m)}>
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="fw-bold table-light">
                    <td colSpan={2} className="text-end">Total</td>
                    <td className="text-end">{money(totals.assigned)}</td>
                    <td className="text-end">{money(totals.booking)}</td>
                    <td className="text-end">{money(totals.sales)}</td>
                    <td className="text-end">{money(totals.open)}</td>
                    <td className="text-center"><span className={`pi-badge ${pctClass(totalPct)}`}>{totalPct}%</span></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        <div className="pi-modal-footer">
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
